import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SEO } from '../components/SEO';
import { ArrowLeft } from 'lucide-react';

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-white" data-testid="career-consults-hero">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link 
            to="/giving-back" 
            className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-medium mb-8 hover:gap-3 transition-all"
          >
            <ArrowLeft size={18} />
            Back to #GivingBack
          </Link>
          
          <span className="inline-block px-3 py-1 bg-[#faf9f7] text-[#d4782c] font-dm-sans font-semibold text-sm mb-4">
            2024
          </span>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a1a1a] mb-4">
            Career Consults
          </h1>
          <p className="text-xl md:text-2xl text-[#d4782c] font-dm-sans font-medium">
            #GivingBack 2024
          </p>
        </div>
      </div>
    </section>
  );
};

// Content Section
const ContentSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-12 md:py-16 bg-[#faf9f7]" data-testid="career-consults-content">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="space-y-6 text-[#4a4744] leading-relaxed">
            <p>
              In May 2024, on the 14th anniversary of his arrival in Australia, Nadir Khan opened his calendar and offered free 30-minute one-on-one consultations to anyone seeking guidance on organisational agility. The sessions ran every weekday for three weeks, from 13 to 31 May, and were delivered via Zoom.
            </p>
            <p>
              The response was immediate. Every available slot was booked out within minutes of the announcement.
            </p>
            <p>
              The conversations spanned career pivots, industry positioning, team dynamics, and navigating complex organisational environments. Each session was personal, confidential, and shaped by over two decades of cross-industry experience.
            </p>
            <p>
              There was only one condition: that those who received help would, in time, pay it forward by helping someone else.
            </p>
            <p>
              It was the second year of the #GivingBack initiative, and it deepened the commitment established in 2023: share what you know, with anyone willing to learn, and ask only that they do the same.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Section (LinkedIn embedded post)
const VideoSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-12 md:py-16 bg-white" data-testid="career-consults-video">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex justify-center">
            <iframe 
              src="https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:7192491191949561856?compact=1" 
              height="399" 
              width="504" 
              frameBorder="0" 
              allowFullScreen="" 
              title="Career Consults 2024 LinkedIn announcement"
              className="max-w-full border border-[#e5e2dc]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-16 md:py-20 bg-[#faf9f7]">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#4a4744] mb-6">
            Interested in future #GivingBack initiatives?
          </p>
          <Link 
            to="/giving-back#get-involved"
            className="inline-block bg-[#1a1a1a] text-white px-8 py-4 font-dm-sans font-semibold hover:bg-[#333] transition-colors"
          >
            Get Involved
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function CareerConsultsPage() {
  return (
    <Layout>
      <SEO 
        title="Career Consults | #GivingBack 2024"
        path="/giving-back/career-consults-2024"
        description="In May 2024, Nadir Khan offered free 30-minute one-on-one consultations to anyone seeking guidance on organisational agility. Every slot was booked within minutes."
      />
      <HeroSection />
      <ContentSection />
      <VideoSection />
      <CTASection />
    </Layout>
  );
}
